import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { apiBaseUrl } from '../config/env';
import {
  Session,
  Project,
  ApiResponse,
  PaginatedResponse,
  SessionQuery,
  UxIssue,
  UxIssueSummary,
  AggregatedIssue,
  IssueStats,
  Variant,
  Experiment,
  MetricResult,
  ResultsSummary,
  TimelineDataPoint,
  LeaderboardEntry,
  FeedbackSummary,
  MonitoredIssue,
  ResolutionTimelinePoint,
  AutoRolloutSettings,
  RolloutQueueItem,
  RolloutHistoryItem,
  Report,
  ExecutiveSummary,
  AiApiKeyInfo,
  AuthSessionData,
  ApiKeySummary,
  CreateApiKeyResponse,
  Journey,
  JourneyStats,
} from '../types';

const API_BASE_URL = apiBaseUrl;

/**
 * Validate a `returnTo` redirect target before forwarding it to the backend.
 * Only same-origin paths or full URLs whose origin matches `window.location.origin`
 * are allowed; anything else (cross-origin, javascript:, data:) is dropped to
 * prevent open-redirect phishing.
 */
function sanitizeReturnTo(returnTo: string | undefined): string | undefined {
  if (!returnTo) return undefined;
  if (typeof window === 'undefined') return undefined;
  try {
    const url = new URL(returnTo, window.location.origin);
    if (url.origin === window.location.origin) {
      return url.pathname + url.search + url.hash;
    }
  } catch {
    // fall through
  }
  return undefined;
}

class ApiService {
  private api: AxiosInstance;
  private _onUnauthorized: (() => void) | null = null;
  private _logoutPending = false;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Auth0 uses cookies via withCredentials; no Bearer token to attach.
    this.api.interceptors.request.use((config) => {
      config.withCredentials = true;
      return config;
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          const message = error.response?.data?.message || '';

          // Don't logout for password change attempts
          const isPasswordAttempt = url.includes('/api-keys/change-password') || url.includes('/api-keys/regenerate');
          const isInvalidPassword = message.includes('Invalid current password') || message.includes('Invalid password');
          // Don't redirect for auth config or me checks
          const isAuthCheck = url.includes('/auth/me') || url.includes('/auth/config');

          if (!(isPasswordAttempt && isInvalidPassword) && !isAuthCheck) {
            // Debounce: only trigger logout once, not per concurrent request
            if (!this._logoutPending) {
              this._logoutPending = true;
              // Use callback to update React state instead of hard refresh
              if (this._onUnauthorized) {
                this._onUnauthorized();
              }
              // Reset debounce flag after a short delay
              setTimeout(() => { this._logoutPending = false; }, 1000);
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Register a callback for when an unauthorized response is received.
  // This allows React to handle the state transition instead of a hard page reload.
  onUnauthorized(callback: () => void): void {
    this._onUnauthorized = callback;
  }

  // Auth0: get the login redirect URL (used for direct navigation).
  getAuth0LoginUrl(returnTo?: string): string {
    const base = API_BASE_URL.replace(/\/api\/v1$/, '');
    const safe = sanitizeReturnTo(returnTo);
    const params = safe ? `?returnTo=${encodeURIComponent(safe)}` : '';
    return `${base}/api/v1/auth/login${params}`;
  }

  // Auth0: redirect into the login flow, optionally targeting the signup screen.
  beginAuth0Login(options?: { returnTo?: string; screenHint?: 'signup' }): void {
    const params = new URLSearchParams();

    const safe = sanitizeReturnTo(options?.returnTo);
    if (safe) {
      params.set('returnTo', safe);
    }

    if (options?.screenHint) {
      params.set('screen_hint', options.screenHint);
    }

    const url = `${API_BASE_URL}/auth/login${params.toString() ? `?${params.toString()}` : ''}`;
    window.location.assign(url);
  }

  // Auth0: check if user has a valid session (cookie-based).
  async getAuth0Session(): Promise<ApiResponse<AuthSessionData>> {
    const response: AxiosResponse<ApiResponse<AuthSessionData>> = await this.api.get('/auth/me');
    return response.data;
  }

  // Auth0: logout. Returns the Auth0 logout URL the caller should redirect to, or null.
  async auth0Logout(returnTo?: string): Promise<string | null> {
    try {
      const safe = sanitizeReturnTo(returnTo);
      const response: AxiosResponse<ApiResponse<{ logoutUrl?: string }>> =
        await this.api.post('/auth/logout', { returnTo: safe });
      if (response.data?.success && response.data.data?.logoutUrl) {
        return response.data.data.logoutUrl;
      }
    } catch {
      // Ignore errors during logout
    }
    return null;
  }

  // Auth0 is the only auth path; the user is considered authenticated when /auth/me succeeds.
  // Callers that need a real check should use getAuth0Session() instead.
  isAuthenticated(): boolean {
    return true;
  }

  // Auth0 is the only auth path; the dashboard never needs an API key in the
  // browser. The previous implementation persisted `apiKey` in localStorage,
  // which would have allowed any reflected XSS to exfiltrate the key. We now
  // never read or write it; ingestion API keys are managed via the API-keys
  // page and surfaced server-side.
  getApiKey(): string | null {
    return null;
  }

  // Legacy no-op kept so callers that still invoke clearCredentials() during logout don't break.
  // Also removes any stale `apiKey` left over from the previous implementation.
  clearCredentials(): void {
    try {
      localStorage.removeItem('apiKey');
    } catch {
      // ignore
    }
  }

  // API Key management (Auth0 mode only — cookies authenticate the request).
  async listApiKeys(): Promise<ApiResponse<ApiKeySummary[]>> {
    const response: AxiosResponse<ApiResponse<ApiKeySummary[]>> = await this.api.get('/api-keys');
    return response.data;
  }

  async createApiKey(data: { name: string; description?: string; label?: string }): Promise<ApiResponse<CreateApiKeyResponse>> {
    const response: AxiosResponse<ApiResponse<CreateApiKeyResponse>> = await this.api.post('/api-keys', data);
    return response.data;
  }

  async deleteApiKey(apiKeyId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/api-keys/${encodeURIComponent(apiKeyId)}`);
    return response.data;
  }

  // Projects API
  async getProjects(): Promise<ApiResponse<Project[]>> {
    const response: AxiosResponse<ApiResponse<Project[]>> = await this.api.get('/projects');
    return response.data;
  }

  async getProjectById(id: string): Promise<ApiResponse<Project>> {
    const response: AxiosResponse<ApiResponse<Project>> = await this.api.get(`/projects/${id}`);
    return response.data;
  }

  async createProject(data: { name: string; description?: string }): Promise<ApiResponse<Project>> {
    const response: AxiosResponse<ApiResponse<Project>> = await this.api.post('/projects', data);
    return response.data;
  }

  async updateProject(
    id: string,
    data: {
      description?: string;
      sessionRetentionDays?: number;
      approvalSettings?: {
        mode?: 'single' | 'team_lead' | 'n_approvals';
        requiredApprovals?: number;
        autoOpenPR?: boolean;
      };
    },
  ): Promise<ApiResponse<Project>> {
    const response: AxiosResponse<ApiResponse<Project>> = await this.api.patch(`/projects/${id}`, data);
    return response.data;
  }

  async getProjectStorageStats(id: string): Promise<ApiResponse<{
    sessionCount: number;
    estimatedSizeMB: number;
    oldestSession: string | null;
    newestSession: string | null;
    retentionDays: number;
    sessionsExpiringSoon: number;
  }>> {
    const response = await this.api.get(`/projects/${id}/storage-stats`);
    return response.data;
  }

  async deleteProject(id: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/projects/${id}`);
    return response.data;
  }

  // Sessions API
  async getSessions(query: Partial<SessionQuery> = {}): Promise<PaginatedResponse<Session>> {
    const params = new URLSearchParams();

    if (query.cursor) params.append('cursor', query.cursor);
    if (query.page) params.append('page', query.page.toString());
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.sortBy) params.append('sortBy', query.sortBy);
    if (query.sortOrder) params.append('sortOrder', query.sortOrder);
    if (query.projectId) params.append('projectId', query.projectId);
    if (query.userId) params.append('userId', query.userId);
    if (query.startDate) params.append('startDate', query.startDate);
    if (query.endDate) params.append('endDate', query.endDate);

    // If no projectId is specified, use the /all endpoint
    const endpoint = query.projectId ? `/sessions?${params.toString()}` : `/sessions/all?${params.toString()}`;
    const response: AxiosResponse<PaginatedResponse<Session>> = await this.api.get(endpoint);
    return response.data;
  }

  async getSessionById(sessionId: string, projectId: string): Promise<ApiResponse<Session>> {
    const response: AxiosResponse<ApiResponse<Session>> = await this.api.get(`/sessions/${sessionId}?projectId=${projectId}`);
    return response.data;
  }

  async getAllSessions(query: Omit<SessionQuery, 'projectId'> = {}): Promise<PaginatedResponse<Session>> {
    const params = new URLSearchParams();
    
    if (query.page) params.append('page', query.page.toString());
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.sortBy) params.append('sortBy', query.sortBy);
    if (query.sortOrder) params.append('sortOrder', query.sortOrder);
    if (query.userId) params.append('userId', query.userId);
    if (query.startDate) params.append('startDate', query.startDate);
    if (query.endDate) params.append('endDate', query.endDate);
    if (query.search) params.append('search', query.search);

    const response: AxiosResponse<PaginatedResponse<Session>> = await this.api.get(`/sessions/all?${params.toString()}`);
    return response.data;
  }

  async deleteSession(sessionId: string, projectId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/sessions/${sessionId}?projectId=${projectId}`);
    return response.data;
  }

  // Detection API
  async analyzeSession(sessionId: string, projectId: string): Promise<ApiResponse<{ sessionId: string; projectId: string; issuesFound: number; issues: UxIssue[] }>> {
    const response: AxiosResponse<ApiResponse<{ sessionId: string; projectId: string; issuesFound: number; issues: UxIssue[] }>> = await this.api.post(`/detection/analyze/${sessionId}?projectId=${projectId}`);
    return response.data;
  }

  async analyzeProjectSessions(projectId: string): Promise<ApiResponse<{ projectId: string; sessionsAnalyzed: number; totalIssuesFound: number }>> {
    const response: AxiosResponse<ApiResponse<{ projectId: string; sessionsAnalyzed: number; totalIssuesFound: number }>> = await this.api.post(`/detection/analyze?projectId=${projectId}`);
    return response.data;
  }

  async reprocessProject(projectId: string): Promise<ApiResponse<{ totalSessions: number; processedSessions: number; totalIssuesFound: number; totalAggregatedUpdated: number; errors: string[]; durationMs: number }>> {
    const response: AxiosResponse<ApiResponse<{ totalSessions: number; processedSessions: number; totalIssuesFound: number; totalAggregatedUpdated: number; errors: string[]; durationMs: number }>> = await this.api.post(`/detection/reprocess?projectId=${projectId}`, {}, { timeout: 300000 });
    return response.data;
  }

  async getIssues(params: { projectId: string; type?: string; severity?: string; sessionId?: string; page?: number; limit?: number }): Promise<PaginatedResponse<UxIssue>> {
    const searchParams = new URLSearchParams();
    searchParams.append('projectId', params.projectId);
    if (params.type) searchParams.append('type', params.type);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.sessionId) searchParams.append('sessionId', params.sessionId);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());

    const response: AxiosResponse<PaginatedResponse<UxIssue>> = await this.api.get(`/detection/issues?${searchParams.toString()}`);
    return response.data;
  }

  async getIssueSummary(projectId: string): Promise<ApiResponse<UxIssueSummary>> {
    const response: AxiosResponse<ApiResponse<UxIssueSummary>> = await this.api.get(`/detection/issues/summary?projectId=${projectId}`);
    return response.data;
  }
  // Detection Settings API
  async getDetectionSettings(projectId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/detection/settings?projectId=${projectId}`);
    return response.data;
  }

  async updateDetectionSettings(projectId: string, settings: Record<string, any>): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/detection/settings?projectId=${projectId}`, settings);
    return response.data;
  }

  async resetDetectionSettings(projectId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/detection/settings?projectId=${projectId}`);
    return response.data;
  }

  // Aggregated Issues API
  async getAggregatedIssues(params: {
    /** Optional — when omitted, the backend widens to every accessible project. */
    projectId?: string;
    type?: string;
    severity?: string;
    status?: string;
    pageUrl?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    cursor?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<AggregatedIssue>> {
    const searchParams = new URLSearchParams();
    if (params.projectId) searchParams.append('projectId', params.projectId);
    if (params.type) searchParams.append('type', params.type);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.status) searchParams.append('status', params.status);
    if (params.pageUrl) searchParams.append('pageUrl', params.pageUrl);
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    if (params.cursor) searchParams.append('cursor', params.cursor);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());

    const response: AxiosResponse<PaginatedResponse<AggregatedIssue>> = await this.api.get(`/issues?${searchParams.toString()}`);
    return response.data;
  }

  async getAggregatedIssueById(id: string): Promise<ApiResponse<AggregatedIssue & { rawDetections: UxIssue[] }>> {
    const response: AxiosResponse<ApiResponse<AggregatedIssue & { rawDetections: UxIssue[] }>> = await this.api.get(`/issues/${id}`);
    return response.data;
  }

  async updateIssueStatus(id: string, status: 'open' | 'resolved' | 'ignored'): Promise<ApiResponse<AggregatedIssue>> {
    const response: AxiosResponse<ApiResponse<AggregatedIssue>> = await this.api.patch(`/issues/${id}/status`, { status });
    return response.data;
  }

  async bulkUpdateIssueStatus(ids: string[], status: 'open' | 'resolved' | 'ignored'): Promise<ApiResponse<{ modifiedCount: number }>> {
    const response: AxiosResponse<ApiResponse<{ modifiedCount: number }>> = await this.api.post('/issues/bulk-status', { ids, status });
    return response.data;
  }

  async getIssueStats(projectId?: string): Promise<ApiResponse<IssueStats>> {
    // When no projectId is supplied, the backend rolls up across every
    // accessible project (cross-project Issues view).
    const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    const response: AxiosResponse<ApiResponse<IssueStats>> = await this.api.get(`/issues/stats${qs}`);
    return response.data;
  }

  // Variant methods
  async getVariants(params: { projectId: string; issueId?: string; status?: string; variantType?: string; sortBy?: string; sortOrder?: 'asc' | 'desc'; cursor?: string; page?: number; limit?: number }): Promise<PaginatedResponse<Variant>> {
    const searchParams = new URLSearchParams();
    searchParams.append('projectId', params.projectId);
    if (params.issueId) searchParams.append('issueId', params.issueId);
    if (params.status) searchParams.append('status', params.status);
    if (params.variantType) searchParams.append('variantType', params.variantType);
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    if (params.cursor) searchParams.append('cursor', params.cursor);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    const response: AxiosResponse<PaginatedResponse<Variant>> = await this.api.get(`/variants?${searchParams.toString()}`);
    return response.data;
  }

  async getVariant(id: string): Promise<ApiResponse<Variant>> {
    const response: AxiosResponse<ApiResponse<Variant>> = await this.api.get(`/variants/${id}`);
    return response.data;
  }

  async generateVariants(issueId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/variants/generate/${issueId}`, {}, { timeout: 120000 });
    return response.data;
  }

  async updateVariantStatus(id: string, status: string, reviewerNotes?: string): Promise<ApiResponse<Variant>> {
    const response: AxiosResponse<ApiResponse<Variant>> = await this.api.patch(`/variants/${id}/status`, { status, reviewerNotes });
    return response.data;
  }

  async getVariantScreenshots(variantId: string): Promise<ApiResponse<{ before: string | null; after: string | null; status: string }>> {
    const response: AxiosResponse<ApiResponse<{ before: string | null; after: string | null; status: string }>> = await this.api.get(`/variants/${variantId}/screenshots`);
    return response.data;
  }

  async retryVariantScreenshots(variantId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/variants/${variantId}/retry-screenshots`);
    return response.data;
  }

  async getVariantPreviewData(variantId: string): Promise<ApiResponse<{ html: string; changes: any[]; selector: string; viewport: { width: number; height: number } }>> {
    const response: AxiosResponse<ApiResponse<{ html: string; changes: any[]; selector: string; viewport: { width: number; height: number } }>> = await this.api.get(`/variants/${variantId}/preview-data`);
    return response.data;
  }

  // Collaboration methods
  async getComments(variantId: string): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/collaboration/comments?variantId=${variantId}`);
    return response.data;
  }

  async createComment(variantId: string, projectId: string, body: string, parentId?: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/collaboration/comments', { variantId, projectId, body, parentId });
    return response.data;
  }

  async deleteComment(id: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/collaboration/comments/${id}`);
    return response.data;
  }

  async getActivity(params: { variantId?: string; projectId?: string; limit?: number }): Promise<ApiResponse<any[]>> {
    const searchParams = new URLSearchParams();
    if (params.variantId) searchParams.append('variantId', params.variantId);
    if (params.projectId) searchParams.append('projectId', params.projectId);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/collaboration/activity?${searchParams.toString()}`);
    return response.data;
  }
  // Git repository methods
  async getGitRepos(projectId: string): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/git/repos?projectId=${projectId}`);
    return response.data;
  }

  async connectGitRepo(data: { projectId: string; owner: string; repo: string; accessToken?: string; defaultBranch?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/git/repos', data);
    return response.data;
  }

  async testGitRepo(id: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/git/repos/${id}/test`);
    return response.data;
  }

  async updateGitRepo(id: string, data: { defaultBranch?: string; pathMappings?: any[]; accessToken?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(`/git/repos/${id}`, data);
    return response.data;
  }

  async deleteGitRepo(id: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/git/repos/${id}`);
    return response.data;
  }

  // GitHub OAuth methods
  async getGitHubAuthUrl(): Promise<ApiResponse<{ url: string }>> {
    const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.api.get('/git/auth/github/url');
    return response.data;
  }

  async getGitHubAuthStatus(): Promise<ApiResponse<{ connected: boolean; githubUsername?: string; avatarUrl?: string }>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/git/auth/github/status');
    return response.data;
  }

  async getGitHubUserRepos(search?: string, page?: number): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (page) params.append('page', page.toString());
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/git/auth/github/repos?${params.toString()}`);
    return response.data;
  }

  async disconnectGitHub(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete('/git/auth/github');
    return response.data;
  }

  // Code generation methods
  async generateCode(variantId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/code-gen/generate/${variantId}`, {}, { timeout: 300000 });
    return response.data;
  }

  async getCodeChanges(variantId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/code-gen/${variantId}`);
    return response.data;
  }

  // Pull request methods
  async createPullRequest(variantId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/pull-requests/create/${variantId}`, {}, { timeout: 60000 });
    return response.data;
  }

  async getPRStatus(variantId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/pull-requests/${variantId}`);
    return response.data;
  }

  // Experiment methods
  async getExperiments(params: { projectId: string; status?: string; page?: number; limit?: number }): Promise<PaginatedResponse<Experiment>> {
    const searchParams = new URLSearchParams();
    searchParams.append('projectId', params.projectId);
    if (params.status) searchParams.append('status', params.status);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    const response: AxiosResponse<PaginatedResponse<Experiment>> = await this.api.get(`/experiments?${searchParams.toString()}`);
    return response.data;
  }

  async getExperiment(id: string): Promise<ApiResponse<Experiment>> {
    const response: AxiosResponse<ApiResponse<Experiment>> = await this.api.get(`/experiments/${id}`);
    return response.data;
  }

  async createExperiment(data: { variantId: string; name: string; description?: string; hypothesis?: string; metrics?: any[]; trafficSplitPercent?: number; minimumSampleSize?: number; significanceThreshold?: number }): Promise<ApiResponse<Experiment>> {
    const response: AxiosResponse<ApiResponse<Experiment>> = await this.api.post('/experiments', data);
    return response.data;
  }

  async startExperiment(id: string): Promise<ApiResponse<Experiment>> {
    const response: AxiosResponse<ApiResponse<Experiment>> = await this.api.post(`/experiments/${id}/start`);
    return response.data;
  }

  async pauseExperiment(id: string): Promise<ApiResponse<Experiment>> {
    const response: AxiosResponse<ApiResponse<Experiment>> = await this.api.post(`/experiments/${id}/pause`);
    return response.data;
  }

  async stopExperiment(id: string, winner?: string, reason?: string): Promise<ApiResponse<Experiment>> {
    const response: AxiosResponse<ApiResponse<Experiment>> = await this.api.post(`/experiments/${id}/stop`, { winner, reason });
    return response.data;
  }

  async getExperimentResults(id: string): Promise<ApiResponse<{ experiment: Experiment; results: MetricResult[] }>> {
    const response: AxiosResponse<ApiResponse<{ experiment: Experiment; results: MetricResult[] }>> = await this.api.get(`/experiments/${id}/results`);
    return response.data;
  }

  // Results Dashboard methods
  async getResultsSummary(params: { projectId: string; startDate?: string; endDate?: string }): Promise<ApiResponse<ResultsSummary>> {
    const searchParams = new URLSearchParams();
    searchParams.append('projectId', params.projectId);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    const response: AxiosResponse<ApiResponse<ResultsSummary>> = await this.api.get(`/experiments/dashboard/summary?${searchParams.toString()}`);
    return response.data;
  }

  async getResultsTimeline(params: { projectId: string; experimentId?: string; startDate?: string; endDate?: string; granularity?: string }): Promise<ApiResponse<{ dataPoints: TimelineDataPoint[] }>> {
    const searchParams = new URLSearchParams();
    searchParams.append('projectId', params.projectId);
    if (params.experimentId) searchParams.append('experimentId', params.experimentId);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    if (params.granularity) searchParams.append('granularity', params.granularity);
    const response: AxiosResponse<ApiResponse<{ dataPoints: TimelineDataPoint[] }>> = await this.api.get(`/experiments/dashboard/timeline?${searchParams.toString()}`);
    return response.data;
  }

  async getResultsLeaderboard(params: { projectId: string; startDate?: string; endDate?: string; limit?: number }): Promise<ApiResponse<LeaderboardEntry[]>> {
    const searchParams = new URLSearchParams();
    searchParams.append('projectId', params.projectId);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    const response: AxiosResponse<ApiResponse<LeaderboardEntry[]>> = await this.api.get(`/experiments/dashboard/leaderboard?${searchParams.toString()}`);
    return response.data;
  }

  // Feedback Loop methods
  async getFeedbackSummary(projectId: string): Promise<ApiResponse<FeedbackSummary>> {
    const response: AxiosResponse<ApiResponse<FeedbackSummary>> = await this.api.get(`/feedback-loop/summary/${projectId}`);
    return response.data;
  }

  async getMonitoredIssues(projectId: string): Promise<ApiResponse<MonitoredIssue[]>> {
    const response: AxiosResponse<ApiResponse<MonitoredIssue[]>> = await this.api.get(`/feedback-loop/monitored/${projectId}`);
    return response.data;
  }

  async getFeedbackTimeline(projectId: string): Promise<ApiResponse<ResolutionTimelinePoint[]>> {
    const response: AxiosResponse<ApiResponse<ResolutionTimelinePoint[]>> = await this.api.get(`/feedback-loop/timeline/${projectId}`);
    return response.data;
  }

  async checkRecurrence(issueId: string): Promise<ApiResponse<{ recurred: boolean; newDetections: number; fixDurability: string }>> {
    const response: AxiosResponse<ApiResponse<{ recurred: boolean; newDetections: number; fixDurability: string }>> = await this.api.post(`/feedback-loop/check-recurrence/${issueId}`);
    return response.data;
  }

  async getIssueResolution(issueId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/feedback-loop/resolution/${issueId}`);
    return response.data;
  }

  // Auto-Rollout methods
  async getAutoRolloutSettings(projectId: string): Promise<ApiResponse<AutoRolloutSettings>> {
    const response: AxiosResponse<ApiResponse<AutoRolloutSettings>> = await this.api.get(`/auto-rollout/settings/${projectId}`);
    return response.data;
  }

  async updateAutoRolloutSettings(projectId: string, settings: Partial<AutoRolloutSettings>): Promise<ApiResponse<AutoRolloutSettings>> {
    const response: AxiosResponse<ApiResponse<AutoRolloutSettings>> = await this.api.patch(`/auto-rollout/settings/${projectId}`, settings);
    return response.data;
  }

  async getAutoRolloutQueue(projectId: string): Promise<ApiResponse<RolloutQueueItem[]>> {
    const response: AxiosResponse<ApiResponse<RolloutQueueItem[]>> = await this.api.get(`/auto-rollout/queue/${projectId}`);
    return response.data;
  }

  async approveRollout(experimentId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/auto-rollout/approve/${experimentId}`);
    return response.data;
  }

  async rejectRollout(experimentId: string, reason?: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/auto-rollout/reject/${experimentId}`, { reason });
    return response.data;
  }

  async getRolloutHistory(projectId: string): Promise<ApiResponse<RolloutHistoryItem[]>> {
    const response: AxiosResponse<ApiResponse<RolloutHistoryItem[]>> = await this.api.get(`/auto-rollout/history/${projectId}`);
    return response.data;
  }

  // Reporting methods
  async generateReport(projectId: string, periodStart: string, periodEnd: string, title?: string): Promise<ApiResponse<Report>> {
    const response: AxiosResponse<ApiResponse<Report>> = await this.api.post(`/reports/generate/${projectId}`, { periodStart, periodEnd, title });
    return response.data;
  }

  async getReportHistory(projectId: string): Promise<ApiResponse<Report[]>> {
    const response: AxiosResponse<ApiResponse<Report[]>> = await this.api.get(`/reports/history/${projectId}`);
    return response.data;
  }

  async getReport(reportId: string): Promise<ApiResponse<Report>> {
    const response: AxiosResponse<ApiResponse<Report>> = await this.api.get(`/reports/${reportId}`);
    return response.data;
  }

  async exportReportCSV(reportId: string): Promise<void> {
    const response = await this.api.get(`/reports/${reportId}/csv`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${reportId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  async getExecutiveSummary(projectId: string, periodStart?: string, periodEnd?: string): Promise<ApiResponse<ExecutiveSummary>> {
    const searchParams = new URLSearchParams();
    if (periodStart) searchParams.append('periodStart', periodStart);
    if (periodEnd) searchParams.append('periodEnd', periodEnd);
    const qs = searchParams.toString();
    const response: AxiosResponse<ApiResponse<ExecutiveSummary>> = await this.api.get(`/reports/executive-summary/${projectId}${qs ? '?' + qs : ''}`);
    return response.data;
  }

  // AI API Keys (BYOM)
  async getAiKeys(projectId: string): Promise<ApiResponse<AiApiKeyInfo[]>> {
    const response: AxiosResponse<ApiResponse<AiApiKeyInfo[]>> = await this.api.get(`/ai-keys?projectId=${projectId}`);
    return response.data;
  }

  async saveAiKey(provider: string, apiKey: string, projectId: string): Promise<ApiResponse<AiApiKeyInfo>> {
    const response: AxiosResponse<ApiResponse<AiApiKeyInfo>> = await this.api.put(`/ai-keys/${provider}`, { apiKey, projectId });
    return response.data;
  }

  async deleteAiKey(provider: string, projectId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/ai-keys/${provider}?projectId=${projectId}`);
    return response.data;
  }

  // ----- Variant refinement -----

  async regenerateVariants(issueId: string, feedback?: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/variants/regenerate/${issueId}`,
      { feedback, captureScreenshots: true },
      { timeout: 120000 },
    );
    return response.data;
  }

  // ----- Generated tests (per variant) -----

  async generateTests(variantId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/test-gen/generate/${variantId}`,
      {},
      { timeout: 300000 },
    );
    return response.data;
  }

  async getGeneratedTests(variantId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/test-gen/${variantId}`);
    return response.data;
  }

  // ----- Journeys -----

  async getJourneys(params: {
    projectId: string;
    frictionLevel?: string;
    status?: string;
    minScore?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    cursor?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Journey[]>> {
    const query = new URLSearchParams();
    query.set('projectId', params.projectId);
    if (params.frictionLevel) query.set('frictionLevel', params.frictionLevel);
    if (params.status) query.set('status', params.status);
    if (params.minScore !== undefined) query.set('minScore', String(params.minScore));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    const response: AxiosResponse<PaginatedResponse<Journey[]>> = await this.api.get(
      `/journeys?${query.toString()}`,
    );
    return response.data;
  }

  async getJourney(id: string): Promise<ApiResponse<Journey>> {
    const response: AxiosResponse<ApiResponse<Journey>> = await this.api.get(`/journeys/${id}`);
    return response.data;
  }

  async getJourneyStats(projectId: string): Promise<ApiResponse<JourneyStats>> {
    const response: AxiosResponse<ApiResponse<JourneyStats>> = await this.api.get(
      `/journeys/stats?projectId=${projectId}`,
    );
    return response.data;
  }

  async analyzeJourneys(
    projectId: string,
    options?: { generateNarrative?: boolean; limit?: number },
  ): Promise<ApiResponse<{ projectId: string; analyzed: number; highFriction: number }>> {
    const response: AxiosResponse<
      ApiResponse<{ projectId: string; analyzed: number; highFriction: number }>
    > = await this.api.post('/journeys/analyze', { projectId, ...options });
    return response.data;
  }

  /** Re-narrate a single session. Hits the per-session endpoint so the
   *  Re-analyze button on the detail page acts on THIS journey, not on an
   *  arbitrary session from the project's distinct-sessionId list. */
  async analyzeJourney(
    sessionId: string,
    projectId: string,
    options?: { generateNarrative?: boolean },
  ): Promise<ApiResponse<Journey>> {
    const generateNarrative = options?.generateNarrative ?? true;
    const response: AxiosResponse<ApiResponse<Journey>> = await this.api.post(
      `/journeys/analyze/${encodeURIComponent(sessionId)}?projectId=${encodeURIComponent(projectId)}`,
      { projectId, generateNarrative },
      { timeout: 120000 },
    );
    return response.data;
  }

  async updateJourneyStatus(id: string, status: string): Promise<ApiResponse<Journey>> {
    const response: AxiosResponse<ApiResponse<Journey>> = await this.api.patch(
      `/journeys/${id}/status`,
      { status },
    );
    return response.data;
  }
}

export const apiService = new ApiService();
